import { FormattedMessage, useIntl } from 'react-intl';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { useOrganizationStaticTicketTitles } from '@/hooks/useStaticTicketTitles';
import { IconEdit, IconTrash, IconWarningTriangle } from '@/icons';
import IconButton from '../core/IconButton';
import { FreePeriodBadge } from './FreePeriodBadge';
import { PeriodStatusBadge } from './PeriodStatusBadge';
import { PeriodTicketKind } from './PeriodTicketKind';
import type { OptionsType } from '../core/Dropdown';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';

interface PeriodListItemProps {
  claim: CollectionTicketClaimDTO;
  collectionType?: string;
  revokeStrategyOptions: OptionsType[];
  onEdit: (claim: CollectionTicketClaimDTO) => void;
  onDelete: (claim: CollectionTicketClaimDTO) => void;
  overlapping?: boolean;
  readOnly?: boolean;
}

export const PeriodListItem = ({
  claim,
  collectionType,
  revokeStrategyOptions,
  onEdit,
  onDelete,
  overlapping = false,
  readOnly = false,
}: PeriodListItemProps) => {
  const { formatMessage } = useIntl();
  const ticketTitle = useOrganizationStaticTicketTitles(collectionType);
  const status = getPeriodStatus(claim);
  const canEdit = !readOnly && status !== 'past';
  const canDelete = !readOnly && status === 'upcoming';

  return (
    <li className="flex flex-col gap-1 rounded-md border border-background-300 px-4 py-3">
      <div className="flex w-full flex-wrap items-center gap-x-5">
        <PeriodDateRange
          from={claim.valid_from}
          to={claim.valid_to}
          className="font-medium"
        />
        <PeriodStatusBadge status={status} />
        {claim.non_invoiced && <FreePeriodBadge />}
        {overlapping && status !== 'past' && (
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-yellow-500 px-2 py-0.5 text-sm font-medium text-contrast-yellow">
            <IconWarningTriangle className="size-4" aria-hidden="true" />
            <FormattedMessage defaultMessage="Överlappar" />
          </span>
        )}
        <div className="ml-auto flex gap-1">
          {canEdit && (
            <IconButton
              variant="ghost"
              onClick={() => onEdit(claim)}
              aria-label={formatMessage({
                defaultMessage: 'Redigera period',
              })}
              icon={<IconEdit className="size-4" />}
            />
          )}
          {canDelete && (
            <IconButton
              variant="ghost"
              onClick={() => onDelete(claim)}
              aria-label={formatMessage({
                defaultMessage: 'Radera period',
              })}
              color="red"
              icon={<IconTrash className="size-4" />}
            />
          )}
        </div>
      </div>
      <span className="text-text-700">
        <FormattedMessage
          defaultMessage="Makulering: {strategy}"
          values={{
            strategy:
              revokeStrategyOptions.find(
                (o) => o.value === claim.revoke_strategy,
              )?.label ?? claim.revoke_strategy,
          }}
        />
        {' · '}
        <PeriodTicketKind
          relational={claim.relational}
          staticTicketName={ticketTitle(claim.stina_product_id)}
        />
      </span>
    </li>
  );
};
